import React, { useEffect, useMemo, useState } from 'react';
import Comment, { IComment } from './Comment';
import { CommentCategory } from './constants';
import { GET_CURRENT_USER_me } from '../../graphql/types/GET_CURRENT_USER';

export interface ICommentListProps {
  comments: IComment[];
  commentCategory?: CommentCategory;
  user: GET_CURRENT_USER_me;
  initialVisible?: number;
  loadMoreStep?: number;
}

const DEFAULT_INITIAL_VISIBLE = 2;
const DEFAULT_LOAD_MORE_STEP = 5;

const CommentList: React.FunctionComponent<ICommentListProps> = (props) => {
  const {
    comments = [],
    commentCategory,
    user,
    initialVisible = DEFAULT_INITIAL_VISIBLE,
    loadMoreStep = DEFAULT_LOAD_MORE_STEP
  } = props;

  const sortedComments = useMemo(() => {
    return [...comments].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [comments]);

  const [visibleCount, setVisibleCount] = useState(initialVisible);

  useEffect(() => {
    setVisibleCount((current) => {
      if (sortedComments.length <= initialVisible) {
        return initialVisible;
      }
      return Math.min(Math.max(current, initialVisible), sortedComments.length);
    });
  }, [sortedComments.length, initialVisible]);

  const canLoadMore = visibleCount < sortedComments.length;
  const canShowLess = visibleCount > initialVisible;
  const visibleComments = sortedComments.slice(-visibleCount);
  const remainingCount = sortedComments.length - visibleCount;

  return (
    <div className="comments m-b-1">
      {canLoadMore && (
        <button
          type="button"
          className="comments-load-more"
          onClick={() =>
            setVisibleCount((count) =>
              Math.min(count + loadMoreStep, sortedComments.length)
            )
          }
        >
          {remainingCount === 1
            ? 'Load 1 more comment'
            : `Load more comments (${remainingCount})`}
        </button>
      )}

      {visibleComments.map((comment) => (
        <Comment
          key={comment.id}
          {...comment}
          commentCategory={commentCategory}
          user={user}
        />
      ))}

      {canShowLess && !canLoadMore && (
        <button
          type="button"
          className="comments-load-more"
          onClick={() => setVisibleCount(initialVisible)}
        >
          Show less
        </button>
      )}
    </div>
  );
};

CommentList.defaultProps = {
  commentCategory: CommentCategory.project
};

export default CommentList;
